import type { Settings, Page } from 'types';
import type { BlockReviews } from 'types/block-reviews';
import type { StateLawsListResult } from './state-law';
import type { VpdPage } from 'types/acf';
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import type { QueryError } from '../api';
import type { JobsByTeamResult } from './jobs';
import type { LocationItems } from './locations';
import type { PartnersReviewsResult, ReviewsListResult } from './reviews';
import type { GetDashboardInitialData } from './dashboard';
import type { GetInitialStrainsData } from './strains';
import jwt from 'jsonwebtoken';
import camelcaseKeys from 'camelcase-keys';
import { initializeWpApollo } from 'lib/apollo';
import { request } from 'lib/rest';
import { getStateLawsList } from './state-law';
import { getLanguageCodeFilter, getSettings } from './settings';
import { getPartnersLocations } from './partners';
import { getJobsByTeam } from './jobs';
import { getPartnersReviews } from './reviews';
import { getDashboardInitialData } from './dashboard';
import { getInitialStrainsData } from './strains';

export type AllStatePagesResult = {
  slug: string[];
  locale: string;
}[];

export async function getAllStatePages(): Promise<AllStatePagesResult> {
  try {
    const { error, data } = await request<{ slug: string; locale: string }[]>(
      `/state-pages`,
    );

    if (error) {
      throw { error };
    }

    // make slug an array and filter the empty strings
    // e.g. '/medical-card/ohio' -> ['medical-card', 'ohio']
    return data.items.map(({ slug, locale }) => {
      return {
        slug: slug.split('/').filter(Boolean),
        locale,
      };
    });
  } catch (error) {
    console.log(`error getting all state pages:`, JSON.stringify(error));

    throw error;
  }
}

export async function getPageData({
  slug,
  locale,
  preview,
}: {
  slug: string;
  locale: string;
  preview?: boolean;
}): Promise<Page> {
  const fields =
    'id,title,date,modified,slug,link,content,seo,acf,translations,language,type';
  const { error, data } = await request<Page[]>(
    `/pages?path=${slug}&_fields=${fields}${preview ? '&status=draft' : ''}`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw { error };
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type FrontPage = {
  page: Page;
  settings: Settings;
  partnersReviews: PartnersReviewsResult['partnersReviews'];
  isPreview: boolean;
};

export async function getFrontPage({
  locale,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<FrontPage>> {
  try {
    const [homepage, settings] = await Promise.all([
      getPageData({ locale, slug: 'homepage', preview }),
      getSettings(locale),
    ]);

    if (!homepage) {
      return {
        notFound: true,
      };
    }

    if (!settings) {
      return {
        notFound: true,
      };
    }

    let partnersReviewsData = null;

    const reviewsBlock = homepage.acf.blocks?.find(
      ({ acfFcLayout }) => acfFcLayout == 'reviews',
    ) as BlockReviews;
    const isTrustPilotWidget = reviewsBlock ? reviewsBlock.trustpilot : false;

    if (reviewsBlock && !isTrustPilotWidget) {
      const client = initializeWpApollo();
      const { error: partnersReviewsError, partnersReviews } =
        await getPartnersReviews({ client });

      if (partnersReviewsError) {
        throw partnersReviewsError;
      }

      partnersReviewsData = partnersReviews;
    }

    return {
      props: {
        settings: {
          ...settings,
          pageTranslations: homepage.translations,
        },
        page: homepage,
        partnersReviews: partnersReviewsData,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting home:`, error);

    throw error;
  }
}

export type PageProps = {
  page: Page;
  settings: Settings;
  dashboard?: GetDashboardInitialData;
  lawStatuses?: StateLawsListResult;
  locations?: LocationItems;
  reviews?: ReviewsListResult;
  partnersReviews?: PartnersReviewsResult['partnersReviews'];
  strains?: GetInitialStrainsData;
  teams?: JobsByTeamResult['teams'];
  vpdEvents?: VpdPage;
  variant?: 'test' | 'control';
  siteTitle?: string;
  isPreview: boolean;
};

export async function getPage({
  locale,
  params,
  preview,
  variant = 'control',
}: GetStaticPropsContext & { variant?: 'control' | 'test' }): Promise<
  GetStaticPropsResult<PageProps>
> {
  const slug = Array.isArray(params.slug) ? params.slug.join('/') : params.slug;

  try {
    const languageCode = getLanguageCodeFilter(locale);

    const [page, settings] = await Promise.all([
      getPageData({ locale, slug, preview }),
      getSettings(locale),
    ]);

    if (!page || !settings) {
      return {
        notFound: true,
      };
    }

    const pageLanguage = page.language;

    // redirect locale page to correct URL
    // ex: /slug-in-spanish -> /es/slug-in-spanish
    if (!preview && pageLanguage !== locale) {
      return page.slug
        ? {
            redirect: {
              permanent: true,
              destination: page.link,
            },
          }
        : {
            notFound: true,
          };
    }

    const pageType = page.acf.pageType;
    const reviewsBlock =
      page.acf.blocks &&
      (page.acf.blocks.find(
        ({ acfFcLayout }) => acfFcLayout == 'reviews',
      ) as BlockReviews);
    const isTrustPilotWidget = reviewsBlock ? reviewsBlock.trustpilot : false;

    let pageData: PageProps = {
      page,
      settings: {
        ...settings,
        pageTranslations: page.translations,
      },
      isPreview: !!preview,
    };

    if (reviewsBlock && !isTrustPilotWidget) {
      const client = initializeWpApollo();
      const { error: partnersReviewsError, partnersReviews } =
        await getPartnersReviews({ client });

      if (partnersReviewsError) {
        throw partnersReviewsError;
      }
      pageData = { ...pageData, partnersReviews: partnersReviews };
    }

    if (pageType === 'careers') {
      const { teams } = await getJobsByTeam();

      pageData = { ...pageData, teams };
    }

    if (pageType === 'partners_map') {
      const client = initializeWpApollo();
      const { error: partnerLocationsError, locations } =
        await getPartnersLocations({
          client,
          languageCode,
        });

      if (partnerLocationsError) {
        throw partnerLocationsError;
      }

      pageData = {
        ...pageData,
        locations,
      };
    }

    if (pageType === 'dashboard') {
      const client = initializeWpApollo();
      const dashboard = await getDashboardInitialData({
        client,
        languageCode,
        locale,
      });

      if ((dashboard as QueryError)?.error) {
        throw (dashboard as QueryError)?.error;
      }

      pageData = {
        ...pageData,
        dashboard: dashboard as GetDashboardInitialData,
      };
    }

    if (pageType === 'strains_archive') {
      const client = initializeWpApollo();
      const strains = await getInitialStrainsData({
        client,
        languageCode,
      });

      if ((strains as QueryError)?.error) {
        throw (strains as QueryError)?.error;
      }

      pageData = { ...pageData, strains: strains as GetInitialStrainsData };
    }

    if (pageType === 'laws') {
      const lawStatuses = await getStateLawsList(locale);
      pageData = { ...pageData, lawStatuses };
    }

    if (pageType === 'affiliate_landing') {
      pageData = {
        ...pageData,
        vpdEvents: pageData?.page?.acf?.vpdPage,
      };
    }

    return {
      props: {
        ...pageData,
        variant,
      },
    };
  } catch (error) {
    console.log(`error getting page ${slug}:`, JSON.stringify(error));

    throw error;
  }
}

export function getResearchIframeUrl(): string | null {
  let token = '';

  try {
    token = jwt.sign(
      {
        resource: { dashboard: 51 },
        params: {},
      },
      process.env.METABASE_SECRET_KEY,
    );

    return `${process.env.METABASE_SITE_URL}/embed/dashboard/${token}#bordered=false&titled=false`;
  } catch (error) {
    console.log(error);

    return null;
  }
}
